import type React from 'react';

const HEX_TO_TAILWIND: Record<string, string> = {
  '#ef4444': 'bg-red-100 text-red-700 border-red-200',
  '#dc2626': 'bg-red-100 text-red-700 border-red-200',
  '#b91c1c': 'bg-red-100 text-red-700 border-red-200',
  '#f97316': 'bg-orange-100 text-orange-700 border-orange-200',
  '#ea580c': 'bg-orange-100 text-orange-700 border-orange-200',
  '#eab308': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  '#ca8a04': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  '#f59e0b': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  '#d97706': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  '#84cc16': 'bg-lime-100 text-lime-700 border-lime-200',
  '#22c55e': 'bg-green-100 text-green-700 border-green-200',
  '#16a34a': 'bg-green-100 text-green-700 border-green-200',
  '#15803d': 'bg-green-100 text-green-700 border-green-200',
  '#10b981': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  '#3b82f6': 'bg-blue-100 text-blue-700 border-blue-200',
  '#6b7280': 'bg-gray-100 text-gray-700 border-gray-200',
};

/**
 * config criteria의 hex color를 Tailwind 배경/텍스트/테두리 클래스로 변환
 * 사전 정의 매핑 우선, 미매칭 시 null 반환 (호출처에서 inline style fallback 적용)
 */
export function hexToTailwindStyle(hex: string | undefined | null): string | null {
  if (!hex) return null;
  const normalized = hex.toLowerCase().trim();
  return HEX_TO_TAILWIND[normalized] ?? null;
}

/**
 * config criteria의 hex를 inline style 객체로 변환 (Tailwind 미매칭 시 fallback)
 * rgba 배경 + hex 텍스트/테두리 색상
 */
export function hexToInlineStyle(hex: string | undefined | null): React.CSSProperties | null {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.15)`,
    color: hex,
    borderColor: hex,
  };
}

/**
 * hex → Tailwind 클래스 (매칭) 또는 inline style (미매칭) 중 하나를 반환
 * className과 style을 동시에 처리해야 하는 컴포넌트에서 사용
 */
export function hexToColorProps(hex: string | undefined | null): {
  className: string;
  style?: React.CSSProperties;
} {
  if (!hex) return { className: 'bg-gray-100 text-gray-700 border-gray-200' };
  const tailwind = hexToTailwindStyle(hex);
  if (tailwind) return { className: tailwind };
  const inline = hexToInlineStyle(hex);
  if (inline) return { className: 'border', style: inline };
  return { className: 'bg-gray-100 text-gray-700 border-gray-200' };
}
